import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../services/firebase';
import Layout from '../component/Layout';

const OrderDelivered = () => {
  const [groupedOrders, setGroupedOrders] = useState({});

  const num = (v) => parseFloat(v) || 0;

  // Determine metal type from purity
  const getMetalType = (purity) => {
    if (!purity) return 'Unknown';
    const p = purity.toString().toUpperCase();
    if (p.includes('925') || p.includes('STERLING') || p === '999') return 'Silver';
    if (p.includes('92') || p.includes('916') || p.includes('22K') || p.includes('24K') || p === '995') return 'Gold';
    return 'Other';
  };

  const calculateProductTotals = (product) => {
    const totalWestage = num(product.buyerDeskCalculation) +
      num(product.wastage) +
      num(product.wastageOperator) +
      num(product.wastageJmi) +
      num(product.specificationMC) +
      (product.specification && !['Plane', 'PLANE'].includes(product.specification)
        ? num(product.specificationMakingJmi) + num(product.specificationMakingOperator)
        : 0);

    const setMcBase = num(product.setMc);
    const setMc = setMcBase + setMcBase * ((num(product.setMakingJmi) + num(product.setMakingOperator)) / 100);

    const gramMcPerGram = num(product.netGramMc) +
      num(product.netGramMc) * ((num(product.gramMakingJmi) + num(product.gramMakingOperator)) / 100);

    const specMcPerGram = num(product.specificationGramRate) +
      num(product.specificationGramRate) * ((num(product.specificationGramRateJmi) + num(product.specificationGramRateOperator)) / 100);

    const netWeight = num(product.netWt);
    const specificationWeight = num(product.specificationWt);

    const TotalAmount = setMc + gramMcPerGram * netWeight + specMcPerGram * specificationWeight;
    const fineWeight = (netWeight * totalWestage) / 100;

    return {
      totalWestage,
      setMc,
      gramMcPerGram,
      specMcPerGram,
      TotalAmount,
      fineWeight: fineWeight.toFixed(3),
      netWeight,
      specificationWeight,
      metalType: getMetalType(product.purity),
    };
  };

  const calculateGroupTotals = (orders) => {
    let totalSets = 0;
    let totalGrossWt = 0;
    let totalNetWt = 0;
    let totalFine = 0;
    let totalAmount = 0;

    // Metal totals
    let goldWeight = 0;
    let silverWeight = 0;
    let otherMetalWeight = 0;

    // RTGS total
    let rtgsTotal = 0;

    orders.forEach((order) => {
      const sets = order.selectedVariants?.reduce((sum, v) => sum + num(v.quantity), 0) || 1;
      const calc = calculateProductTotals(order);

      totalSets += sets;
      totalGrossWt += num(order.grossWt);
      totalNetWt += num(order.netWt);
      totalFine += num(calc.fineWeight);
      totalAmount += calc.TotalAmount;

      // Metal weight accumulation
      if (calc.metalType === 'Gold') {
        goldWeight += num(calc.fineWeight);
      } else if (calc.metalType === 'Silver') {
        silverWeight += num(calc.fineWeight);
      } else {
        otherMetalWeight += num(calc.fineWeight);
      }

      // RTGS check: assuming RTGS orders have paymentMode = 'RTGS'
      if (order.paymentMode === 'RTGS' || order.isRtgs) {
        rtgsTotal += calc.TotalAmount;
      }
    });

    return {
      totalSets: totalSets.toFixed(3),
      totalGrossWt: totalGrossWt.toFixed(3),
      totalNetWt: totalNetWt.toFixed(3),
      totalFine: totalFine.toFixed(3),
      totalAmount: totalAmount.toFixed(2),
      goldWeight: goldWeight.toFixed(3),
      silverWeight: silverWeight.toFixed(3),
      otherMetalWeight: otherMetalWeight.toFixed(3),
      rtgsTotal: rtgsTotal.toFixed(2),
    };
  };

  const formatDate = (date) => date ? date.toLocaleString('en-IN') : '—';

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const q = query(
          collection(db, 'orderList'),
          where('orderStatus', 'in', ['Delivered', 'paymentDeliveredToSeller', 'paymentDeliveredtoSeller', 'PaymentSettled', 'paymentSettled'])
        );
        const querySnapshot = await getDocs(q);
        const ordersData = [];

        querySnapshot.forEach((doc) => {
          const data = doc.data();
          let orderTime = null;
          if (data.orderTime?.seconds) {
            orderTime = new Date(data.orderTime.seconds * 1000);
          } else if (data.addedAt) {
            orderTime = new Date(data.addedAt);
          }
          ordersData.push({ id: doc.id, ...data, orderTime });
        });

        const groups = {};
        ordersData.forEach((order) => {
          const buyer = order.buyerId || 'Unknown Buyer';
          if (!groups[buyer]) groups[buyer] = { buyer, orders: [] };
          groups[buyer].orders.push(order);
        });

        Object.keys(groups).forEach((key) => {
          groups[key].orders.sort((a, b) => (a.orderTime || 0) - (b.orderTime || 0));
        });

        setGroupedOrders(groups);
      } catch (error) {
        console.error('Error fetching delivered orders:', error);
      }
    };

    fetchOrders();
  }, []);

  const orderEntries = Object.entries(groupedOrders);

  return (
    <Layout pageTitle="Order Delivered">
      <div style={{ padding: '16px' }}>
        {orderEntries.length === 0 ? (
          <p style={{
            textAlign: 'center',
            color: '#6b7280',
            paddingTop: '32px',
            fontSize: '18px'
          }}>
            No delivered orders.
          </p>
        ) : (
          orderEntries.map(([buyer, group]) => {
            const totals = calculateGroupTotals(group.orders);
            return (
              <div key={buyer} style={{ marginBottom: '24px', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
                {/* Group Header with Extended Totals */}
                <div style={{ backgroundColor: '#f9fafb', padding: '12px 16px', borderBottom: '2px solid #1f2937' }}>
                  <h3 style={{ margin: 0, color: '#1f2937' }}>{buyer}</h3>
                  <div style={{ display: 'flex', gap: '12px', marginTop: '6px', flexWrap: 'wrap', fontSize: '13px' }}>
                    <span><strong>Orders:</strong> {group.orders.length}</span>
                    <span><strong>Total Sets:</strong> {totals.totalSets}</span>
                    <span><strong>Gross Wt:</strong> {totals.totalGrossWt}g</span>
                    <span><strong>Net Wt:</strong> {totals.totalNetWt}g</span>
                    <span><strong>Fine Wt:</strong> {totals.totalFine}g</span>
                    <span><strong>Total Value:</strong> ₹{totals.totalAmount}</span>
                    
                    {/* Metal Totals */}
                    {parseFloat(totals.goldWeight) > 0 && (
                      <span style={{ color: '#d97706' }}><strong>Gold:</strong> {totals.goldWeight}g</span>
                    )}
                    {parseFloat(totals.silverWeight) > 0 && (
                      <span style={{ color: '#64748b' }}><strong>Silver:</strong> {totals.silverWeight}g</span>
                    )}
                    {parseFloat(totals.otherMetalWeight) > 0 && (
                      <span><strong>Other Metal:</strong> {totals.otherMetalWeight}g</span>
                    )}

                    {/* RTGS Total */}
                    {parseFloat(totals.rtgsTotal) > 0 && (
                      <span style={{ color: '#0d9488' }}><strong>RTGS:</strong> ₹{totals.rtgsTotal}</span>
                    )}
                  </div>
                </div>

                {/* Orders Table */}
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ minWidth: '100%', borderCollapse: 'collapse', border: '2px solid #1f2937' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f3f4f6' }}>
                        <th style={tableHeaderStyle}>Order Time</th>
                        <th style={tableHeaderStyle}>Product</th>
                        <th style={tableHeaderStyle}>Metal</th>
                        <th style={tableHeaderStyle}>Image</th>
                        <th style={tableHeaderStyle}>Seller</th>
                        <th style={tableHeaderStyle}>Net Wt (g)</th>
                        <th style={tableHeaderStyle}>Fine Wt (g)</th>
                        <th style={tableHeaderStyle}>Amount (₹)</th>
                        <th style={tableHeaderStyle}>Payment</th>
                        <th style={tableHeaderStyle}>Variants</th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.orders.map((order) => {
                        const calc = calculateProductTotals(order);
                        return (
                          <tr key={order.id}>
                            <td style={tableCellStyle}>{formatDate(order.orderTime)}</td>
                            <td style={{ ...tableCellStyle, fontWeight: 'bold' }}>{order.productName || '—'}</td>
                            <td style={tableCellStyle}>{calc.metalType}</td>
                            <td style={tableCellStyle}>
                              {order.images?.[0]?.url ? (
                                <img
                                  src={order.images[0].url.trim()}
                                  alt="Product"
                                  style={{ height: '40px', width: '40px', objectFit: 'contain', border: '1px solid #d1d5db', borderRadius: '4px' }}
                                />
                              ) : (
                                <span style={{ color: '#9ca3af' }}>—</span>
                              )}
                            </td>
                            <td style={tableCellStyle}>{order.sellerId || '—'}</td>
                            <td style={tableCellStyle}>{num(order.netWt).toFixed(3)}</td>
                            <td style={tableCellStyle}>{calc.fineWeight}</td>
                            <td style={tableCellStyle}>{calc.TotalAmount.toFixed(2)}</td>
                            <td style={tableCellStyle}>
                              {order.paymentMode === 'RTGS' || order.isRtgs ? 'RTGS' : order.paymentMode || '—'}
                            </td>
                            <td style={tableCellStyle}>
                              {order.selectedVariants?.length ? (
                                order.selectedVariants.map((v, i) => (
                                  <div key={i} style={{ whiteSpace: 'nowrap' }}>
                                    {v.size} × {v.quantity}
                                  </div>
                                ))
                              ) : (
                                <span>—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })
        )}
      </div>
    </Layout>
  );
};

const tableHeaderStyle = {
  border: '2px solid #1f2937',
  padding: '8px 12px',
  textAlign: 'left',
  fontSize: '13px',
  fontWeight: 'bold',
  backgroundColor: '#f3f4f6',
  color: '#4b5563',
};

const tableCellStyle = {
  border: '2px solid #1f2937',
  padding: '8px 12px',
  fontSize: '14px',
};

export default OrderDelivered;